// GC4 — wallet-balance pill rendered in TopBar.
// Uses the direct /api/billing/wallet endpoint (GC2) via useWallet().
// Hidden when not signed in, loading, or on 401/404 errors.

import { Link } from 'react-router-dom'
import { Zap } from 'lucide-react'
import { useWallet } from '@/hooks/useWallet'
import { getAuthToken } from '@/lib/api'

// ─── Skeleton pill ────────────────────────────────────────────────────────────

function WalletSkeleton() {
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 animate-pulse"
      aria-busy="true"
      aria-label="Loading wallet balance"
    >
      <div className="h-3.5 w-3.5 rounded bg-white/10" />
      <div className="h-3 w-10 rounded bg-white/10" />
    </div>
  )
}

// ─── WalletBadge ─────────────────────────────────────────────────────────────

export function WalletBadge() {
  const isSignedIn = !!getAuthToken()
  const { data, isLoading } = useWallet()

  // Not signed in — render nothing
  if (!isSignedIn) return null

  // Fetching — show skeleton
  if (isLoading) return <WalletSkeleton />

  // API returned no data or wallet doesn't exist yet — render nothing silently
  if (!data || !data.wallet_exists) return null

  // Show the canonical spendable (top-up + remaining allowance), matching the
  // Billing "AVAILABLE" card — NOT the top-up bucket alone, which under-counted
  // a paid user's allowance. Falls back to balance_usd if available is absent.
  const availableDisplay = data.available_usd ?? data.balance_usd
  const balanceDisplay = `$${availableDisplay}`

  return (
    <Link
      to="/billing"
      data-testid="dynamic"
      aria-label={`Wallet balance: ${availableDisplay} USD. Click for billing.`}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:border-lime/30 hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime/50"
    >
      <Zap className="h-3.5 w-3.5 text-[#ccff00] shrink-0" aria-hidden="true" />
      <span className="text-xs font-medium text-white tabular-nums">
        {balanceDisplay}
      </span>
      <span className="hidden sm:inline text-[10px] text-gray-400">balance</span>
    </Link>
  )
}
